import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, Circle } from '@phosphor-icons/react';

import { defaultTheme } from 'theSource/theme';
import { Button } from 'theSource/components';
import { ROUTES } from 'enrollment/app/routes';
import {
  setActiveHeader,
  setMaxHeaderItem,
  selectHeaderItems,
} from './services';
import {
  EnrollmentContext,
  useConfigurationQuery,
  useEnrollmentContextQuery,
} from 'theSource/queries';
import { EnrollmentNavMobileDrawer } from '../EnrollmentNavMobileDrawer';
import { getMaxHeaderIndex } from './utils';
import {
  selectEnrollmentLatest,
  selectProfileSteps,
} from 'enrollment/components/BottomNavigation/services';
import {
  EnrollmentNavVisibleStyled,
  TabletBGStyled,
  DrawerVisibleStyled,
} from '../../HeaderStyled';
import { selectEnrollmentContext } from 'theSource/features/Home/components/EnrollmentBanner/services';
import { ChatBot } from 'components/ChatBot';
import { selectIsAdmin } from 'theSource/features';
import { AdminOptions } from '../AdminOptions';

export function getStatusIcon(
  index: number,
  maxIndex: number,
  activeIndex: number
) {
  if (index === activeIndex || index === maxIndex) {
    return (
      <Circle
        weight="fill"
        size={24}
        color={defaultTheme.palette.primary.main}
        alt=""
        aria-hidden="true"
      ></Circle>
    );
  } else if (index < maxIndex) {
    return (
      <CheckCircle
        weight="fill"
        size={24}
        color={defaultTheme.palette.primary.main}
        alt=""
        aria-hidden="true"
      ></CheckCircle>
    );
  }
  return <Circle weight="fill" size={24} alt=""></Circle>;
}

export const findPathForNavItem = (itemName: string) => {
  const obj = Object.entries(ROUTES.enrollment).find((enrollmentRoute) => {
    return (
      enrollmentRoute[0] === itemName ||
      (enrollmentRoute[0] === 'benefitDetails' && itemName === 'details')
    );
  });
  if (obj) {
    return obj[1].path;
  } else {
    return '';
  }
};

export const EnrollmentNavigation = ({ chatBot }: { chatBot?: boolean }) => {
  const { t } = useTranslation('enrollment.navigation');
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;

  const enrollmentLatest = useSelector(selectEnrollmentLatest);
  const enrollmentContext = useSelector(selectEnrollmentContext);
  const profileSteps = useSelector(selectProfileSteps);
  const decisionSupportService = profileSteps.decisionSupport;

  const {
    enrollmentContext: enrollmentContexts,
    isLoadingEnrollmentContext,
    isFetchingEnrollmentContext,
  } = useEnrollmentContextQuery({
    enabled: true,
    apiHost: String(lumosApiHost),
  });

  const [
    activeEnrollmentNavigationItemIndex,
    setActiveEnrollmentNavigationItemIndex,
  ] = useState(0);
  const [maxNavigationItemIndex, setMaxNavigationItemIndex] = useState(0);
  useEffect(() => {
    setMaxNavigationItemIndex(0);
  }, [enrollmentContext]);

  const navTopath = (route: string) => {
    navigate(`enrollment/${route}`);
  };

  useEffect(() => {
    const firstLevelPathName = location.pathname.split('/')[2];
    let routeIndex = 0;
    switch (firstLevelPathName) {
      case ROUTES.enrollment.profile.path.substring(1):
      case ROUTES.enrollment.family.path.substring(1):
      case ROUTES.enrollment.questionnaire.path.substring(1):
        routeIndex = 0;
        break;
      case ROUTES.enrollment.decisionSupportRouter.path.substring(1):
        if (decisionSupportService === 'decisioniq') {
          routeIndex = 0;
          break;
        }
        if (decisionSupportService === 'bundles') {
          routeIndex = 1;
          break;
        }
        break;
      case ROUTES.enrollment.benefits.path.substring(1):
      case ROUTES.enrollment.plans.path.substring(1):
        routeIndex = 1;
        break;
      case ROUTES.enrollment.benefitDetails.path.substring(1):
      case ROUTES.enrollment.manageProviders.path.substring(1):
      case ROUTES.enrollment.manageBeneficiaries.path.substring(1):
        routeIndex = 2;
        break;
      case ROUTES.enrollment.review.path.substring(1):
        routeIndex = 3;
        break;
      default:
        routeIndex = -1;
    }

    const headerIndex = Math.max(maxNavigationItemIndex, routeIndex);
    setActiveEnrollmentNavigationItemIndex(routeIndex);
    setMaxNavigationItemIndex(headerIndex);
    dispatch(setMaxHeaderItem(headerIndex));
    dispatch(setActiveHeader(routeIndex));
  }, [
    location.pathname,
    maxNavigationItemIndex,
    dispatch,
    decisionSupportService,
  ]);

  useEffect(() => {
    if (
      enrollmentContexts &&
      !isLoadingEnrollmentContext &&
      !isFetchingEnrollmentContext
    ) {
      const contexts = enrollmentContexts?.data as EnrollmentContext[];
      const activeContextIndex = Math.max(
        contexts.findIndex((context) => {
          return enrollmentContext === context.enrollment_type_lookup_code;
        }),
        0
      );

      setMaxNavigationItemIndex(
        getMaxHeaderIndex(
          enrollmentLatest,
          contexts[activeContextIndex].required_tasks[0]
        )
      );
    }
  }, [
    enrollmentContexts,
    enrollmentContext,
    enrollmentLatest,
    dispatch,
    isLoadingEnrollmentContext,
    isFetchingEnrollmentContext,
  ]);

  const navItems = useSelector(selectHeaderItems);
  const isAdmin = useSelector(selectIsAdmin);

  const isScreenSmall = window.innerWidth < 744;

  return (
    <>
      <EnrollmentNavVisibleStyled
        className="order-md-last col-3 col-md-12 col-lg-6 order-lg-2"
        data-testid="enrollment-navigation-main"
        aria-label="Enrollment Steps"
        role="navigation"
      >
        <TabletBGStyled
          data-testid="enrollment-navigation"
          className="v-center"
        >
          {navItems.map((item, index) => (
            <div key={`navigation-${index}`} className="v-center">
              {index > 0 ? (
                <div key={`divider-${index}`} className="divider ml-1 mr-2">
                  <hr
                    className="divider"
                    aria-hidden="true"
                    role="presentation"
                  />
                </div>
              ) : (
                <></>
              )}
              <Button
                key={index}
                id={`navigate-btn-${t(item)}`}
                className="btn btn-link overline2 px-1"
                data-testid="navigate-button"
                disabled={maxNavigationItemIndex < index}
                startIcon={getStatusIcon(
                  index,
                  maxNavigationItemIndex,
                  activeEnrollmentNavigationItemIndex
                )}
                label={t(item)}
                variant="tertiary"
                type="button"
                onClick={() => navTopath(findPathForNavItem(item))}
                aria-label={`${t('common:navigateTo')} ${t(item)} ${t('common:category')} ${maxNavigationItemIndex > index ? t('common:completed') : ''}`}
              />
            </div>
          ))}
        </TabletBGStyled>
      </EnrollmentNavVisibleStyled>
      <DrawerVisibleStyled
        className={`col-1 order-last ${isAdmin ? 'flex-row-reverse' : ''} align-items-center`}
      >
        {chatBot && <ChatBot renderDefault={false} showText={false} />}

        <EnrollmentNavMobileDrawer
          maxNavigationItemIndex={maxNavigationItemIndex}
          activeEnrollmentNavigationItemIndex={
            activeEnrollmentNavigationItemIndex
          }
        />
        {isAdmin && isScreenSmall && (
          <div>
            <AdminOptions />
          </div>
        )}
      </DrawerVisibleStyled>
    </>
  );
};
